import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ProgressBar from '../../../components/ui/ProgressBar';
import { useLanguage } from '../../../contexts/LanguageContext';

const SiteOverviewCard = ({ site }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (!site) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 text-center">
        <Icon name="Building2" size={32} className="text-muted-foreground mx-auto mb-2" />
        <p className="text-muted-foreground text-sm">{t('siteNoAssigned')}</p>
      </div>
    );
  }

  // Map the compliance enum to a RAG colour. NULL/missing values render
  // grey ("Unknown") rather than falling through to red — a brand-new site
  // hasn't been assessed yet, so the dashboard shouldn't claim it's
  // critical. Per card 47aUEbUC. The label is hard-coded "Unknown" to
  // match ComplianceStatusBadge.jsx; adding a translation key would force
  // a multi-language sweep that's out of scope here.
  const ragStatus = site?.compliance_status === 'compliant' ? 'green'
    : site?.compliance_status === 'warning' ? 'amber'
    : site?.compliance_status === 'critical' ? 'red'
    : 'grey';

  const ragConfig = {
    green: { dot: 'bg-emerald-500', labelKey: 'siteStatusCompliant', badge: 'bg-emerald-100 text-emerald-800' },
    amber: { dot: 'bg-amber-500', labelKey: 'siteStatusAttention', badge: 'bg-amber-100 text-amber-800' },
    red: { dot: 'bg-red-500', labelKey: 'siteStatusCritical', badge: 'bg-red-100 text-red-800' },
    grey: { dot: 'bg-muted-foreground', label: 'Unknown', badge: 'bg-muted text-muted-foreground' },
  };
  const config = ragConfig?.[ragStatus];
  const completion = site?.completion_pct ?? null;

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2.5 h-2.5 rounded-full ${config?.dot}`} />
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config?.badge}`}>
                {config?.labelKey ? t(config?.labelKey) : config?.label}
              </span>
            </div>
            <h2 className="text-xl font-bold text-white font-heading">{site?.site_name}</h2>
            <p className="text-slate-300 text-sm mt-0.5">{site?.address || t('address')}</p>
          </div>
          <button
            onClick={() => navigate(`/sites/${site?.id}`)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white text-xs font-medium transition-colors"
          >
            <Icon name="ExternalLink" size={13} />
            {t('siteFullDetails')}
          </button>
        </div>
      </div>
      <div className="p-6">
        {/* Progress — only render when a real completion value exists */}
        {completion !== null && (
          <div className="mb-5">
            <ProgressBar
              value={completion}
              label={t('siteOverallCompletion')}
              height="h-3"
              color="bg-accent"
            />
          </div>
        )}

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { labelKey: 'siteWorkersOnSite', value: site?.worker_count || 0, icon: 'Users', color: 'text-blue-600 bg-blue-50', onClick: () => navigate(`/sites/${site?.id}?tab=workers`) },
            { labelKey: 'siteClient', value: site?.client_name || 'N/A', icon: 'Briefcase', color: 'text-purple-600 bg-purple-50', onClick: () => {} },
            { labelKey: 'siteStatus', value: site?.status || t('active'), icon: 'Activity', color: 'text-emerald-600 bg-emerald-50', onClick: () => navigate(`/sites/${site?.id}`) },
          ]?.map(stat => (
            <button
              key={stat?.labelKey}
              onClick={stat?.onClick}
              className="bg-muted/50 hover:bg-muted rounded-lg p-3 text-center transition-colors"
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${stat?.color}`}>
                <Icon name={stat?.icon} size={15} />
              </div>
              <p className="text-sm font-semibold text-foreground truncate">{stat?.value}</p>
              <p className="text-xs text-muted-foreground">{t(stat?.labelKey)}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SiteOverviewCard;
